import { EXPLORE_ROUTES } from "./homeDashboard/exploreRoutes";
import { isTopicModule } from './questionBank/qbankNavigation';
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import HomeDashboardLayout from "./homeDashboard/HomeDashboardLayout";
import CommonPageHeader from "./homeDashboard/CommonPageHeader";
import "./dashboard/Dashboard.css";
import "./homeDashboard/HomeDashboard.css";
import "./homeDashboard/DashboardFavoriteExam.css";
import "./homeDashboard/DashboardSkeleton.css";
import "./DashboardQuestionBank.css";
import "./homeDashboard/rapidFire/RapidFireSubject.css";
import SubjectCategorySlider from "./SubjectCategorySlider";
import QuestionBankSubjects from "./QuestionBankSubjects";

import heroImg from "./assets/images/qb1.png";
import coinImage from "./assets/images/coiin.png";
import coinConverterGif from "./assets/images/Coin-converter.gif";

import { useSubjectCategories } from "./hooks/useSubjectCategories";
import { useExamSubjectCategories } from "./hooks/useExamSubjectCategories";
import { setDefaultExamSubjectCategory } from "./services/examCategoryService";
import { useQuestionBankSubjects } from "./hooks/useQuestionBankSubjects";
import { useQuestionBankTopicToggle } from "./hooks/useQuestionBankTopicToggle";
import { useSidebarData } from "./hooks/useSidebarData";
import { buildRapidFireBackTarget } from "./homeDashboard/rapidFire/rapidFireFlowState";
import { RAPID_FIRE_ROUTES } from "./homeDashboard/rapidFire/rapidFireRoutes";
import {
  buildQuestionBankBackTarget,
  buildQuestionBankFlowState,
  buildQuestionBankSubjectsState,
  getStoredQuestionBankFlowState,
  persistQuestionBankFlowState,
} from "./questionBank/qbankFlowState";
import {
  resolveQuestionBankStateId,
  resolveQuestionBankStateName,
} from "./questionBank/stateUtils";

const QUESTION_BANK_CATEGORY_ID = 2;
const LECTURES_CATEGORY_ID = 3;
const NOTES_CATEGORY_ID = 4;
const RAPID_FIRE_CATEGORY_ID = 5;
const GAMIFICATION_CATEGORY_ID = 6;
const FLASH_CARDS_CATEGORY_ID = 9;
const PODCAST_CATEGORY_ID = 12;
const INFOGRAPHICS_CATEGORY_ID = 13;

function SubjectCategorySliderShimmer() {
  return (
    <div className="subject-category-slider-shimmer" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="subject-category-slider-shimmer__item">
          <span className="dashboard-skeleton dashboard-skeleton--icon subject-category-slider-shimmer__icon" />
          <span className="dashboard-skeleton dashboard-skeleton--line subject-category-slider-shimmer__title" />
          <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short subject-category-slider-shimmer__text" />
        </div>
      ))}
    </div>
  );
}

const QuestionBank = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const storedFlowState = useMemo(() => getStoredQuestionBankFlowState(), []);
  const flowState = useMemo(
    () => buildQuestionBankFlowState(location.state, storedFlowState),
    [location.state, storedFlowState]
  );
  const [topicsEnabled, setTopicsEnabled] = useState(false);
  const [hasTopicToggleOverride, setHasTopicToggleOverride] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const selectedCategory = flowState?.selectedCategory || location.state?.selectedCategory || null;
  const isExamCourseFlow = Boolean(location.state?.examFlow);
  const selectedCategoryId = Number(
    selectedCategory?.category_id ||
      selectedCategory?.id ||
      location.state?.category_id ||
      QUESTION_BANK_CATEGORY_ID
  );
  const resolvedCategoryId = Number.isFinite(selectedCategoryId) && selectedCategoryId > 0
    ? selectedCategoryId
    : QUESTION_BANK_CATEGORY_ID;
  const isRapidFireCategory = resolvedCategoryId === RAPID_FIRE_CATEGORY_ID;
  const isNotesCategory = resolvedCategoryId === NOTES_CATEGORY_ID;
  const isLecturesCategory = resolvedCategoryId === LECTURES_CATEGORY_ID;
  const isGamificationCategory = resolvedCategoryId === GAMIFICATION_CATEGORY_ID;
  const isFlashCardsCategory = resolvedCategoryId === FLASH_CARDS_CATEGORY_ID;
  const isPodcastCategory = resolvedCategoryId === PODCAST_CATEGORY_ID;
  const isInfographicsCategory = resolvedCategoryId === INFOGRAPHICS_CATEGORY_ID;
  const { data: sidebarData = null } = useSidebarData();
  const {
    data: subjectCategoriesData,
    isLoading: subjectCategoriesLoading,
    isFetching: subjectCategoriesFetching,
    isError: hasSubjectCategoriesError,
    error: subjectCategoriesError,
  } = useSubjectCategories({
    category_id: resolvedCategoryId,
    states_id: resolveQuestionBankStateId({ locationState: flowState, sidebarData }),
    exam_course_id: null,
    ...(location.state?.free_paid ? { free_paid: location.state.free_paid } : {}),
  }, {
    enabled: !isExamCourseFlow,
  });
  const {
    data: examSubjectCategoriesData,
    isLoading: examSubjectCategoriesLoading,
    isFetching: examSubjectCategoriesFetching,
    isError: hasExamSubjectCategoriesError,
    error: examSubjectCategoriesError,
  } = useExamSubjectCategories(
    location.state?.exams_id,
    resolvedCategoryId,
    location.state?.free_paid || "",
    isExamCourseFlow
  );
  const saveExamSubjectCategoryMutation = useMutation({
    mutationFn: setDefaultExamSubjectCategory,
  });
  const activeSubjectCategoriesData = isExamCourseFlow ? examSubjectCategoriesData : subjectCategoriesData;
  const isActiveSubjectCategoriesLoading = isExamCourseFlow ? examSubjectCategoriesLoading : subjectCategoriesLoading;
  const isActiveSubjectCategoriesFetching = isExamCourseFlow ? examSubjectCategoriesFetching : subjectCategoriesFetching;
  const hasActiveSubjectCategoriesError = isExamCourseFlow ? hasExamSubjectCategoriesError : hasSubjectCategoriesError;
  const activeSubjectCategoriesError = isExamCourseFlow ? examSubjectCategoriesError : subjectCategoriesError;
  const categories = useMemo(
    () => activeSubjectCategoriesData?.subjectCategoryList || [],
    [activeSubjectCategoriesData]
  );
  const defaultCategory = categories.find((item) => Number(item?.is_default) === 1);
  const [resolvedSubjectFilter, setResolvedSubjectFilter] = useState(null);
  const [activeSubjectCategory, setActiveSubjectCategory] = useState(
    () => Number(location.state?.subject_category_id || 0) || "all"
  );
  const [hasResolvedExamSubjectCategory, setHasResolvedExamSubjectCategory] = useState(
    () => !isExamCourseFlow || Number(location.state?.subject_category_id || 0) > 0
  );
  const resolvedStateId = useMemo(
    () =>
      resolveQuestionBankStateId({
        locationState: flowState,
        sidebarData,
      }),
    [flowState, sidebarData]
  );
  const resolvedStateName = useMemo(
    () =>
      resolveQuestionBankStateName({
        locationState: flowState,
        sidebarData,
      }),
    [flowState, sidebarData]
  );
  const selectedBattle = flowState?.selectedBattle || location.state?.selectedBattle || null;
  const subjectRequestParams = useMemo(() => {
    const params = {
      states_id: resolvedStateId,
      category_id: resolvedCategoryId,
      page: currentPage,
      ...(searchTerm.trim() ? { search_query: searchTerm.trim() } : {}),
    };

    if (isGamificationCategory) {
      const battleId = Number(
        selectedBattle?.id || selectedBattle?.battles_id || selectedBattle?.battle_id || 0
      );

      if (battleId > 0) {
        params.battle_id = battleId;
      }
    }

    if (Number(activeSubjectCategory) > 0) {
      params.subject_category_id = Number(activeSubjectCategory);
    }

    if (isExamCourseFlow) {
      params.exams_id = Number(location.state?.exams_id || 0);
      params.free_paid = location.state?.free_paid || "F";
      params.search_query = searchTerm.trim() || undefined;
      params.subject_category_id =
        activeSubjectCategory !== "all"
          ? Number(activeSubjectCategory)
          : undefined;
    }

    return params;
  }, [
    activeSubjectCategory,
    currentPage,
    isGamificationCategory,
    resolvedCategoryId,
    resolvedStateId,
    selectedBattle,
    isExamCourseFlow,
    location.state,
    searchTerm,
  ]);
  const {
    data: questionBankSubjectsData,
    isLoading: questionBankSubjectsLoading,
    isFetching: questionBankSubjectsFetching,
    isError: hasQuestionBankSubjectsError,
    error: questionBankSubjectsError,
  } = useQuestionBankSubjects(subjectRequestParams, {
    enabled: !isActiveSubjectCategoriesLoading && !isActiveSubjectCategoriesFetching && (isExamCourseFlow ? hasResolvedExamSubjectCategory : Boolean(subjectCategoriesData && resolvedSubjectFilter === subjectCategoriesData)),
    examFlow: isExamCourseFlow,
  });
  const questionBankTopicToggleMutation = useQuestionBankTopicToggle(subjectRequestParams);
  const isTopicToggleVisible =
    !isRapidFireCategory &&
    Number(questionBankSubjectsData?.topicVisibilityStatus) === 1 &&
    (!isExamCourseFlow || String(location.state?.free_paid || '').toUpperCase() !== 'F');
  const isTopicToggleSaving = questionBankTopicToggleMutation.isPending;

  useEffect(() => {
    if (!isExamCourseFlow && !isActiveSubjectCategoriesLoading && !isActiveSubjectCategoriesFetching) {
      setResolvedSubjectFilter(subjectCategoriesData);
    }
    if (!categories.length) {
      setActiveSubjectCategory("all");
      return;
    }

    setActiveSubjectCategory((current) => {
      if (current !== "all" && categories.some((item) => Number(item.subject_category_id) === Number(current))) {
        if (isExamCourseFlow) setHasResolvedExamSubjectCategory(true);
        return current;
      }

      const nextCategory = defaultCategory ? Number(defaultCategory.subject_category_id) : "all";
      if (isExamCourseFlow) setHasResolvedExamSubjectCategory(true);
      return nextCategory;
    });
  }, [categories, defaultCategory, isExamCourseFlow, subjectCategoriesData, isActiveSubjectCategoriesLoading, isActiveSubjectCategoriesFetching]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSubjectCategory, searchTerm, resolvedCategoryId]);

  useEffect(() => {
    if (isRapidFireCategory) {
      setTopicsEnabled(false);
      return;
    }

    if (!hasTopicToggleOverride) {
      setTopicsEnabled(Number(questionBankSubjectsData?.topicToggleStatus) === 1);
    }
  }, [hasTopicToggleOverride, isRapidFireCategory, questionBankSubjectsData?.topicToggleStatus]);

  useEffect(() => {
    setHasTopicToggleOverride(false);
  }, [resolvedCategoryId, resolvedStateId]);

  const filteredCategories = useMemo(() => {
    if (isTopicModule(resolvedCategoryId) && !isExamCourseFlow) return categories;
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return categories;
    }

    return categories.filter((item) => {
      const name = String(item?.name || item?.subject_category_name || "").toLowerCase();
      const description = String(item?.description || item?.subject_category_description || "").toLowerCase();

      return name.includes(normalizedSearch) || description.includes(normalizedSearch);
    });
  }, [categories, searchTerm, resolvedCategoryId, isExamCourseFlow]);

  const resolvedSubjectCategoriesError = hasActiveSubjectCategoriesError
    ? activeSubjectCategoriesError?.response?.data?.message || activeSubjectCategoriesError?.message || "Unable to load subject categories."
    : "";
  const resolvedQuestionBankSubjectsError = hasQuestionBankSubjectsError
    ? questionBankSubjectsError?.response?.data?.message || questionBankSubjectsError?.message || "Unable to load subjects."
    : "";
  const isSubjectCategoriesPending = isActiveSubjectCategoriesLoading || isActiveSubjectCategoriesFetching;
  const isQuestionBankSubjectsPending = isSubjectCategoriesPending
    ? false
    : questionBankSubjectsLoading || questionBankSubjectsFetching;
  const categoryName = questionBankSubjectsData?.categoryName || selectedCategory?.title || selectedCategory?.category_name || "Question Bank";
  const headerStateName = questionBankSubjectsData?.stateName || resolvedStateName || "State not selected";
  const todayTotalAttempts = Number(questionBankSubjectsData?.todayTotalAttempts || 0);
  const todayTotalAttemptsLeft = Number(questionBankSubjectsData?.todayTotalAttemptsLeft || 0);
  const todayAttemptLimit = todayTotalAttempts + todayTotalAttemptsLeft;
  const userCoins = Number(questionBankSubjectsData?.userCoins || 0);
  const subjectCount = Number(
    questionBankSubjectsData?.pagination?.totalRecords || questionBankSubjectsData?.subjectList?.length || 0
  );
  const heroImage = questionBankSubjectsData?.bannerImage || heroImg;
  const gamificationCoinAsset = isGamificationCategory ? coinConverterGif : coinImage;
  const selectedBattleTitle = String(
    selectedBattle?.title ||
      selectedBattle?.battles_name ||
      selectedBattle?.name ||
      "Group Battle"
  ).trim();
  const selectedBattleDescription = String(
    selectedBattle?.description ||
      selectedBattle?.battles_description ||
      (selectedBattleTitle ? `Choose Subject for ${selectedBattleTitle}` : "Choose Subject for Battle")
  ).trim();

  const subjectsScreenState = useMemo(
    () =>
      buildQuestionBankFlowState({
        ...flowState,
        selectedState: {
          state_id: resolvedStateId,
          state_name: resolvedStateName,
        },
        selectedCategory: selectedCategory || {
          category_id: resolvedCategoryId,
          id: resolvedCategoryId,
          title: categoryName,
          category_name: categoryName,
        },
        state_id: resolvedStateId,
        state_name: resolvedStateName,
        category_id: resolvedCategoryId,
      }),
    [categoryName, flowState, resolvedCategoryId, resolvedStateId, resolvedStateName, selectedCategory]
  );

  useEffect(() => {
    persistQuestionBankFlowState(subjectsScreenState);
  }, [subjectsScreenState]);
  const categoriesBackTarget = useMemo(
    () => ({
      pathname: EXPLORE_ROUTES.categories,
      state: {
        state_id: resolvedStateId,
        selectedState: {
          state_id: resolvedStateId,
          state_name: resolvedStateName,
        },
        selectedCategory: selectedCategory || {
          category_id: resolvedCategoryId,
          id: resolvedCategoryId,
          title: categoryName,
          category_name: categoryName,
        },
      },
    }),
    [categoryName, resolvedCategoryId, resolvedStateId, resolvedStateName, selectedCategory]
  );
  const handleSubjectsBack = () => {
    if (flowState?.qbankBackTarget?.pathname) {
      navigate(flowState.qbankBackTarget.pathname, {
        state: flowState.qbankBackTarget.state,
        replace: true,
      });
      return;
    }

    navigate(categoriesBackTarget.pathname, {
      state: categoriesBackTarget.state,
      replace: true,
    });
  };
  const handleTopicToggleChange = async (event) => {
    const nextChecked = event.target.checked;

    setTopicsEnabled(nextChecked);
    setHasTopicToggleOverride(true);

    try {
      await questionBankTopicToggleMutation.mutateAsync({
        category_id: resolvedCategoryId,
        topic_toogle_status: nextChecked ? 1 : 0,
        ...(isExamCourseFlow ? { exam_id: Number(location.state?.exams_id || 0) } : {}),
      });
    } catch (_error) {
      setTopicsEnabled(!nextChecked);
      setHasTopicToggleOverride(false);
    }
  };
  return (
    <>
      <style>
        {`
       .homeDashboard-wrapper .page-content {
         padding-right: 0;
       }
      .layout-wrapper.homeDashboard-wrapper {
         background: linear-gradient(to bottom, rgba(103, 78, 221, 0.05) 0%, #fff 100%)!important;
       }
       .subject-category-slider-shimmer {
         display: grid;
         grid-template-columns: repeat(5, minmax(0, 1fr));
         gap: 16px;
       }
       .subject-category-slider-shimmer__item {
         min-height: 128px;
         padding: 18px 16px;
         border-radius: 18px;
         background: #fff;
         border: 1px solid #ece7ff;
         display: flex;
         flex-direction: column;
         gap: 12px;
       }
       .subject-category-slider-shimmer__title {
         width: 72%;
       }
       .subject-category-slider-shimmer__text {
         width: 48%;
       }
       @media (max-width: 1200px) {
         .subject-category-slider-shimmer {
           grid-template-columns: repeat(3, minmax(0, 1fr));
         }
       }
       @media (max-width: 767px) {
         .subject-category-slider-shimmer {
           grid-template-columns: repeat(2, minmax(0, 1fr));
         }
       }
       @media (max-width: 480px) {
         .subject-category-slider-shimmer {
           grid-template-columns: 1fr;
         }
       }
        `}
      </style>
      <HomeDashboardLayout showHeader={false} variant="common-page-header-layout">
        <section className="qb" aria-label={isRapidFireCategory ? "Rapid fire introduction" : isNotesCategory ? "Notes introduction" : isFlashCardsCategory ? "Flash Cards introduction" : isPodcastCategory ? "Podcast introduction" : "Question bank introduction"}>
          <CommonPageHeader embedded stateName={isGamificationCategory ? "Gamification" : headerStateName} onBack={handleSubjectsBack} />

          <div className="qb-body">
            <div className="d-flex align-items-center justify-content-center">
              <div className="qb-copy">
                <h1 className="qb-title">
                  {isRapidFireCategory ? (
                    <>
                      Rapid <span>Fire</span>
                    </>
                  ) : isGamificationCategory ? (
                    <>
                      {selectedBattleTitle.split(/\s+/).slice(0, -1).join(" ") || selectedBattleTitle} <span>{selectedBattleTitle.split(/\s+/).slice(-1).join(" ") || "Battle"}</span>
                    </>
) : isNotesCategory ? (
                    <>
                      <span>Notes</span>
                    </>
                  ) : isLecturesCategory ? (
                    <>
                      <span>Lectures</span>
                    </>
                  ) : isFlashCardsCategory ? (
                    <><span>Flash Cards</span></>
                  ) : isPodcastCategory ? (
                    <><span>Podcasts</span></>
                  ) : isInfographicsCategory ? (
                    <><span>Infographics</span></>
                  ) : (
                    <>
                      Question <span>Bank</span>
                    </>
                  )}
                </h1>

                <p className="qb-sub">{isGamificationCategory ? selectedBattleDescription : categoryName}</p>

                {isRapidFireCategory ? (
                  <div className="leaderboard-attempts">
                    <div 
                      className="la-card leader-card" 
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(RAPID_FIRE_ROUTES.leaderboard, { state: { ...subjectsScreenState, rapidFireBackTarget: buildRapidFireBackTarget(EXPLORE_ROUTES.subjects, buildQuestionBankSubjectsState(subjectsScreenState)) } })}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="la-icon leader-icon">
                        <i className="fa-solid fa-users" />
                      </div>

                      <span>Leaderboard</span>
                    </div>

                    <div className="la-card attemp-card">
                      <div className="la-icon attemp-icon">
                        <i className="fa-solid fa-fire" />
                      </div>

                      <span>
                        Today Attempts <strong>{`${todayTotalAttempts}/${todayAttemptLimit || todayTotalAttempts}`}</strong>
                      </span>
                    </div>
                  </div>
                ) : isGamificationCategory ? (
                  <div className="qb-topics-row subject-gap">
                    <div className="subject-badge">
                      <i className="bi bi-book-half"></i>
                      <span className="subject-text">{`${subjectCount} subjects available`}</span>
                    </div>

                    <div className="game-coin d-flex gap-2 justify-content-center align-items-center">
                      <span><img src={gamificationCoinAsset} alt="" /></span>
                      <span className="coin-valuee fw-bold">{userCoins}</span>
                    </div>
                  </div>
                ) : isTopicToggleVisible ? (
                  <div className="qb-topics-row">
                    <div className="qb-topics">
                      <span className="qb-topics-label">Topics</span>

                      <label className={`qb-toggle ${topicsEnabled ? "is-on" : ""}`}>
                        <input
                          type="checkbox"
                          checked={topicsEnabled}
                          onChange={handleTopicToggleChange}
                          aria-label="Toggle topics"
                          disabled={!isTopicToggleVisible || isTopicToggleSaving}
                        />
                        <span className="qb-toggle-track" aria-hidden="true">
                          <span className="qb-toggle-thumb" />
                        </span>
                      </label>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="qb-visual" aria-hidden="true">
                <div className="qb-dots">
                  {Array.from({ length: 21 }).map((_, index) => (
                    <span key={index} />
                  ))}
                </div>

                <img src={heroImage} alt="" className="qb-hero" />
              </div>
            </div>
            <label className="qb-search" aria-label="Search subjects">
              <i className="bi bi-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search subjects"
                aria-label="Search subjects"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </label>
          </div>
        </section>

        <div className="innerContent dashboardExam">
          <div>
            <div className="quick-action__header">
              <h2 id="quick-action-title" className="quick-action__title">
                Subject Categories
              </h2>
            </div>
            {isSubjectCategoriesPending ? <SubjectCategorySliderShimmer /> : null}
            {!isSubjectCategoriesPending && resolvedSubjectCategoriesError && !categories.length ? <div className="cs-status">{resolvedSubjectCategoriesError}</div> : null}
            {!isSubjectCategoriesPending && !resolvedSubjectCategoriesError ? (
              <SubjectCategorySlider
                categories={filteredCategories}
                allSubjectCount={activeSubjectCategoriesData?.allSubjectCount || 0}
                activeSubject={activeSubjectCategory}
                onSelect={(subjectCategoryId) => {
                  setActiveSubjectCategory(subjectCategoryId);
                  if (isExamCourseFlow) setHasResolvedExamSubjectCategory(true);
                }}
                queryParams={{ category_id: resolvedCategoryId }}
                persistSelection={!isExamCourseFlow}
                onPersistSelection={(subjectCategoryId) => {
                  saveExamSubjectCategoryMutation.mutate({
                    subject_category_id: subjectCategoryId,
                  });
                }}
              />
            ) : null}
          </div>

          <div className="m-20">
            {!isQuestionBankSubjectsPending && resolvedQuestionBankSubjectsError && !(questionBankSubjectsData?.subjectList || []).length ? <div className="cs-status">{resolvedQuestionBankSubjectsError}</div> : null}
            {!resolvedQuestionBankSubjectsError ? (
              <QuestionBankSubjects
                subjects={questionBankSubjectsData?.subjectList || []}
                searchTerm={searchTerm}
                requestParams={subjectRequestParams}
                pagination={questionBankSubjectsData?.pagination || {}}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                isLoading={isQuestionBankSubjectsPending}
                navigationState={subjectsScreenState}
                topicsEnabled={topicsEnabled}
                isRapidFireCategory={isRapidFireCategory}
                isGamificationCategory={isGamificationCategory}
                isExamCourseFlow={isExamCourseFlow}
              />
            ) : null}
          </div>
        </div>
      </HomeDashboardLayout>

    </>
  );
};

export default QuestionBank;


































